// The publishing calendar.
//
// Posts already had a list. A list answers "what have I made"; a calendar
// answers the two questions that actually run a content operation — "what goes
// out this week" and "what is Thursday empty for" — and the second one is only
// answerable if an empty day is something you can *click*. So every blank cell
// here is a control: it opens Compose with that moment already filled in, which
// is what "plan the month" means in practice.
//
// Where a post sits: `scheduled_at` when it has one, `created_at` when it does
// not. A draft made on Tuesday belongs on Tuesday until somebody gives it a
// time — hiding it until then would make the calendar quietly incomplete, which
// is worse than a calendar nobody trusts.
//
// The four views share one data load. The window asked of the server is the
// grid about to be drawn (six weeks for a month, seven days for a week), so
// changing view or month is one round trip and nothing is filtered in the
// browser that the database could have filtered better.
//
// WHAT ELSE IS ON IT. Holidays and festivals from the calendars this person
// subscribes to (migration 20260829000100). They are not decoration: Diwali and
// Mother's Day are the reason half the posts on this grid exist, and a planner
// who has to keep them in another tab is planning with half the information.
// They render as all-day chips, above the posts and never mixed in with them.
//
// AND MOVING A POST. Dragging is the gesture a calendar promises, so it works —
// but it is not the only way, because a drag is unavailable to a keyboard and
// hostile on a phone. The peek panel carries a "Move to" field that does exactly
// the same thing through the same RPC.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  listCalendarPosts, reschedulePost, signMedia, thumbPath, thumbTarget,
} from "../lib/api";
import { usePoll } from "../lib/usePoll";
import {
  listCalendarEvents, listCalendarFeeds, listCalendarSubscriptions,
  type CalendarEvent, type CalendarFeed, type CalendarSubscription,
} from "../lib/calendar";
import { isPaywall, paywallMessage } from "../lib/entitlements";
import { logError } from "../lib/logger";
import { useWorkspace } from "../lib/WorkspaceContext";
import { Spinner } from "../components/Spinner";
import { formatInZone, instantToWallClock, wallClockToInstant, zoneName } from "../lib/timezone";
import { useZone } from "../lib/userSettings";
import { DeliveryRow } from "../components/DeliveryRow";
import { CalendarPicker, chooseVisible, isStale } from "../components/CalendarPicker";
import { FacebookIcon, InstagramIcon, YouTubeIcon } from "../components/PlatformIcons";
import { PLATFORM_LABEL, type Platform, type Post } from "../lib/types";
import "./calendar.css";

type View = "month" | "week" | "day" | "list";

const VIEWS: Array<{ key: View; label: string }> = [
  { key: "month", label: "Month" },
  { key: "week", label: "Week" },
  { key: "day", label: "Day" },
  { key: "list", label: "List" },
];

/** The row height of one hour in the week and day grids. */
const HOUR_PX = 52;
/** How much of the day a post visually occupies. Publishing is an instant, but
 *  a one-pixel event is unclickable, and this is also the overlap window. */
const SLOT_MINUTES = 45;
/** Where the time grid opens. Nobody schedules at 3am, and starting at midnight
 *  means every week view opens on nine empty rows. */
const OPENING_HOUR = 7;
/** A day gets three events before it starts saying "+2 more" instead. */
const MAX_PER_CELL = 3;

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const PLATFORMS: Platform[] = ["facebook", "instagram", "youtube"];

const STATUS_CHIP: Record<Post["status"], string> = {
  draft: "", scheduled: "busy", publishing: "busy",
  published: "ok", partially_published: "warn", failed: "err",
};

const STATUS_LABEL: Record<Post["status"], string> = {
  draft: "Draft", scheduled: "Scheduled", publishing: "Publishing",
  published: "Published", partially_published: "Partly published", failed: "Failed",
};

/** The filter, in the words somebody running a calendar would use. */
const STATUS_FILTERS: Array<{ key: string; label: string; match: (p: Post) => boolean }> = [
  { key: "all", label: "Everything", match: () => true },
  { key: "planned", label: "Planned", match: (p) => p.status === "draft" || p.status === "scheduled" },
  { key: "published", label: "Published", match: (p) => p.status === "published" },
  {
    key: "attention", label: "Needs attention",
    match: (p) => p.status === "failed" || p.status === "partially_published",
  },
];

// ------------------------------------------------------------------ dates
//
// TWO KINDS OF DATE LIVE IN THIS FILE and they must not be confused.
//
//   A GRID DAY is a calendar square. It is built from local year/month/date
//   fields and never read as an instant — `dayKey` reads back exactly the
//   digits it was built from, so the browser's own zone cancels out and the
//   square means "the 14th", nothing more.
//
//   AN INSTANT is what a post carries. It only becomes a day once a zone is
//   named, and the zone that must name it is the reader's chosen one: a calendar
//   drawn in UTC puts an 11pm post in Kolkata on the wrong day, and one drawn in
//   the browser's zone does the same to an agency planning a client's morning
//   from another country. That is what `dayKeyIn`, `hourIn` and `timeText`
//   below are for — every instant on screen goes through one of them.

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const startOfWeek = (d: Date) => addDays(startOfDay(d), -d.getDay());
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const dayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** When a post lands: its release time, or when it was made if it has none. */
const timeOf = (p: Post) => new Date(p.scheduled_at ?? p.created_at);

/** The grid square an instant belongs in, decided by the reader's zone. */
const dayKeyIn = (instant: Date | string, zone: string) =>
  instantToWallClock(instant, zone).slice(0, 10);

/** Which hour lane it sits in, same rule. */
const hourIn = (instant: Date | string, zone: string) =>
  Number(instantToWallClock(instant, zone).slice(11, 13));

/** Minutes past midnight, for placing a chip down a day column. */
const minutesIn = (instant: Date | string, zone: string) => {
  const wall = instantToWallClock(instant, zone);
  return Number(wall.slice(11, 13)) * 60 + Number(wall.slice(14, 16));
};

/**
 * `datetime-local` wants wall-clock text, never an ISO instant.
 *
 * Takes a GRID DAY, so reading local fields is right by construction — see the
 * note at the top. An instant goes through `instantToWallClock` instead.
 */
function localInput(d: Date): string {
  return `${dayKey(d)}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Today's square, in the reader's zone rather than the browser's. */
function todayIn(zone: string): Date {
  const [y, m, d] = dayKeyIn(new Date(), zone).split("-").map(Number);
  return new Date(y, m - 1, d);
}

/**
 * The days on screen, and the window to ask the server for.
 *
 * Month pads out to whole weeks because that is what a month grid draws; list
 * does not, because "August" in a list means August and a stray 31 July would
 * read as a bug.
 */
function gridFor(view: View, anchor: Date): { days: Date[]; from: Date; to: Date } {
  if (view === "day") {
    const from = startOfDay(anchor);
    return { days: [from], from, to: addDays(from, 1) };
  }
  if (view === "week") {
    const from = startOfWeek(anchor);
    return { days: Array.from({ length: 7 }, (_, i) => addDays(from, i)), from, to: addDays(from, 7) };
  }
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const to = addMonths(first, 1);
  if (view === "list") {
    const days: Date[] = [];
    for (let d = first; d < to; d = addDays(d, 1)) days.push(d);
    return { days, from: first, to };
  }
  const from = startOfWeek(first);
  const end = addDays(startOfWeek(addDays(to, -1)), 7);
  const days: Date[] = [];
  for (let d = from; d < end; d = addDays(d, 1)) days.push(d);
  return { days, from, to: end };
}

function headingFor(view: View, anchor: Date, days: Date[]): string {
  if (view === "day") {
    return anchor.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  }
  if (view === "week") {
    const a = days[0], b = days[days.length - 1];
    const sameMonth = a.getMonth() === b.getMonth();
    return `${a.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ` +
      `${b.toLocaleDateString(undefined, sameMonth ? { day: "numeric" } : { month: "short", day: "numeric" })}, ` +
      `${b.getFullYear()}`;
  }
  return anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

const timeText = (instant: Date | string, zone: string) =>
  formatInZone(instant, zone, { hour: "numeric", minute: "2-digit" });

const hourText = (h: number) =>
  new Date(2000, 0, 1, h).toLocaleTimeString(undefined, { hour: "numeric" });

// ------------------------------------------------------------------ pieces

function PlatformMark({ platform }: { platform: Platform }) {
  const Icon = platform === "facebook" ? FacebookIcon
    : platform === "instagram" ? InstagramIcon : YouTubeIcon;
  return <Icon className="platform-icon cal-mark" />;
}

/** What a post is called when it has no title — the first words of the caption. */
function postName(p: Post): string {
  return p.title?.trim() || p.caption.trim().split("\n")[0] || "(no caption)";
}

function platformsOf(p: Post): Platform[] {
  const seen = new Set<Platform>();
  for (const d of p.deliveries ?? []) seen.add(d.platform);
  return PLATFORMS.filter((x) => seen.has(x));
}

// ------------------------------------------------------------------ holidays

/** One day of one subscribed calendar, ready to draw. */
interface Holiday {
  key: string;
  summary: string;
  feedName: string;
  hue: number;
  category: CalendarEvent["category"];
  /** True when this came from the offline base rather than the live feed. */
  offline: boolean;
}

/**
 * Holidays keyed by day, filtered to the categories each subscription asks for.
 *
 * Multi-day events are expanded across every day they cover — Navratri is nine
 * days and belongs on nine cells, not on the first one with an arrow. The
 * expansion is capped: a year-long "event" in somebody's own calendar would
 * otherwise fill the grid, and a calendar full of one thing is a calendar
 * nobody can read.
 */
function holidaysByDay(
  events: CalendarEvent[],
  feeds: Map<string, { name: string; hue: number; categories: string[] }>,
): Map<string, Holiday[]> {
  const out = new Map<string, Holiday[]>();

  for (const e of events) {
    const feed = feeds.get(e.feed_id);
    if (!feed || !feed.categories.includes(e.category)) continue;

    for (let day = e.starts_on, guard = 0; day <= e.ends_on && guard < 45; guard++) {
      const list = out.get(day);
      const chip: Holiday = {
        key: `${e.id}:${day}`,
        summary: e.summary,
        feedName: feed.name,
        hue: feed.hue,
        category: e.category,
        offline: e.source === "builtin",
      };
      if (list) list.push(chip); else out.set(day, [chip]);

      // Date arithmetic on the string, in UTC, so a local DST change cannot
      // skip or repeat a day of a festival.
      const next = new Date(`${day}T00:00:00Z`);
      next.setUTCDate(next.getUTCDate() + 1);
      day = next.toISOString().slice(0, 10);
    }
  }
  return out;
}

/**
 * The all-day row. Clicking one plans a post for that day, because "Diwali is on
 * the 8th" and "put something out on the 8th" are one thought, and making
 * somebody carry the date to another control is where a planning tool starts
 * feeling like paperwork.
 */
function HolidayChips({
  chips, onPlan, max,
}: {
  chips: Holiday[];
  onPlan: ((h: Holiday) => void) | null;
  max?: number;
}) {
  const shown = max ? chips.slice(0, max) : chips;
  return (
    <>
      {shown.map((h) => {
        const title = `${h.summary} · ${h.feedName}${h.offline ? " (offline copy)" : ""}`;
        const className = `cal-holiday swatch ${h.category}`;
        const style = { "--swatch-h": h.hue } as React.CSSProperties;

        return onPlan ? (
          <button key={h.key} className={className} style={style} title={title}
                  onClick={() => onPlan(h)}
                  aria-label={`${h.summary} — plan a post for this day`}>
            {h.summary}
          </button>
        ) : (
          <span key={h.key} className={className} style={style} title={title}>{h.summary}</span>
        );
      })}
      {max && chips.length > max && (
        <span className="cal-holiday more" title={chips.slice(max).map((c) => c.summary).join(", ")}>
          +{chips.length - max}
        </span>
      )}
    </>
  );
}

/** One post, as it appears in a cell or a time grid. */
function EventPill({
  post, thumb, onOpen, className = "", style, draggable = false, onDragStart, busy = false,
}: {
  post: Post;
  thumb?: string;
  onOpen: () => void;
  className?: string;
  style?: React.CSSProperties;
  /** Only for a post that can actually move: draft or scheduled, and the
   *  permission to say so. A handle that lifts and then refuses is worse than
   *  no handle. */
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  /** Mid-move. The ring goes on the thing that started the call. */
  busy?: boolean;
}) {
  const zone = useZone();
  const at = timeOf(post);
  const marks = platformsOf(post);
  return (
    <button
      type="button"
      className={`cal-event status-${post.status} ${className}${draggable ? " movable" : ""}${busy ? " busy" : ""}`}
      style={style}
      onClick={onOpen}
      draggable={draggable}
      onDragStart={onDragStart}
      title={`${timeText(at, zone)} — ${postName(post)}${draggable ? " (drag to move)" : ""}`}
      aria-label={
        `${STATUS_LABEL[post.status]} ${post.kind} at ${timeText(at, zone)}: ${postName(post)}` +
        (marks.length ? `, going to ${marks.map((m) => PLATFORM_LABEL[m]).join(", ")}` : "")
      }
    >
      {busy
        ? <span className="cal-thumb placeholder"><Spinner label="Moving this post" /></span>
        : thumb
          ? <img className="cal-thumb" src={thumb} alt="" loading="lazy" />
          : <span className="cal-thumb placeholder" aria-hidden="true">{post.kind === "video" ? "▶" : "▦"}</span>}
      <span className="cal-event-body">
        <span className="cal-event-time">{timeText(at, zone)}</span>
        <span className="cal-event-title">{postName(post)}</span>
      </span>
      <span className="cal-event-marks" aria-hidden="true">
        {marks.map((m) => <PlatformMark key={m} platform={m} />)}
      </span>
    </button>
  );
}

/**
 * Reading a post without leaving the month you were planning.
 *
 * Deliberately read-only, and deliberately not a second post editor: it shows
 * where the post is going and how that went, and hands off to the post's own
 * screen for anything else.
 */
function PostPeek({
  post, thumb, onClose, onMove, moving = false,
}: {
  post: Post;
  thumb?: string;
  onClose: () => void;
  /** Null when this post cannot move: wrong status, or no `post.schedule`. */
  /** Takes a wall clock in the reader's zone; the caller names the instant. */
  onMove: ((when: string) => Promise<void>) | null;
  moving?: boolean;
}) {
  // The keyboard's route to the thing the mouse does by dragging. Not a
  // fallback bolted on afterwards: on a phone it is the only way, and it is the
  // only way at all for anyone who does not use a pointer.
  const zone = useZone();
  const [when, setWhen] = useState(() =>
    post.scheduled_at ? instantToWallClock(post.scheduled_at, zone) : "");
  const [moveError, setMoveError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const at = timeOf(post);
  const deliveries = post.deliveries ?? [];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal wide" role="dialog" aria-modal="true"
           aria-label={postName(post)} onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          <h2 style={{ margin: 0 }}>{postName(post)}</h2>
          <span className={`chip ${STATUS_CHIP[post.status]}`}>{STATUS_LABEL[post.status]}</span>
        </div>
        <p className="muted" style={{ marginTop: ".35rem" }}>
          {post.scheduled_at
            ? `Goes out ${formatInZone(at, zone)} ${zoneName(zone)}`
            : `Not scheduled — created ${formatInZone(at, zone)}`}
          {" · "}{post.kind}
        </p>

        {thumb && (
          // `thumb` is the derivative now, and a video's derivative is a poster
          // frame — a still, not something with a play head. Rendering it into
          // a <video> would give a control strip over a picture that cannot
          // play. An asset with no derivative still has the original here, so
          // the old behaviour is kept for it.
          post.kind === "video" && !post.media_assets?.preview_path
            ? <video className="preview" src={thumb} controls />
            : <img className="preview" src={thumb} alt="" />
        )}

        {post.caption && (
          <>
            <h3>Caption</h3>
            <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{post.caption}</p>
          </>
        )}

        <h3>Networks</h3>
        {deliveries.length === 0
          ? <p className="muted" style={{ margin: 0 }}>No destinations on this post yet.</p>
          : deliveries.map((d) => <DeliveryRow key={d.id} d={d} />)}

        {onMove && (
          <>
            <h3>Move this post</h3>
            <div className="row cal-move">
              <input
                type="datetime-local"
                value={when}
                aria-label="New time for this post"
                onChange={(e) => { setWhen(e.target.value); setMoveError(null); }}
              />
              <button
                className="primary"
                disabled={moving || !when}
                onClick={async () => {
                  setMoveError(null);
                  try {
                    await onMove(when);
                  } catch (e: any) {
                    setMoveError(e.message ?? String(e));
                  }
                }}
              >
                {moving ? <><Spinner /> Moving…</> : "Move"}
              </button>
            </div>
            {moveError && <p className="problem error" style={{ marginTop: ".5rem" }}>{moveError}</p>}
          </>
        )}

        <div className="row modal-actions">
          <Link className="cal-linkbtn" to={`/posts/${post.id}`}>Open post</Link>
          <span className="spacer" />
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ screen

export function Calendar() {
  // Seeing the calendar is `post.view`; filling a slot is `post.create`. The
  // database refuses independently — this only decides which controls exist
  // (ADR-0011).
  const { active, can, ready } = useWorkspace();
  const workspaceId = active?.workspace_id ?? null;
  const navigate = useNavigate();

  // The view and the date live in the URL, so a planning conversation can be a
  // pasted link rather than "scroll to next March".
  const [params, setParams] = useSearchParams();
  const view = (VIEWS.find((v) => v.key === params.get("view"))?.key ?? "month") as View;
  const anchor = useMemo(() => {
    const raw = params.get("on");
    const parsed = raw ? new Date(`${raw}T00:00:00`) : null;
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date();
  }, [params]);

  const [posts, setPosts] = useState<Post[] | null>(null);
  const [thumbs, setThumbs] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [peek, setPeek] = useState<Post | null>(null);
  const [platformFilter, setPlatformFilter] = useState<Set<Platform>>(new Set());
  const [statusFilter, setStatusFilter] = useState("all");

  // The other calendars. Loaded once for the person, not per window: the
  // subscription list is small and does not change when somebody pages through
  // March.
  const [feeds, setFeeds] = useState<CalendarFeed[]>([]);
  const [subs, setSubs] = useState<CalendarSubscription[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [picking, setPicking] = useState(false);

  // Which post is mid-move, and what the server said if it refused. One at a
  // time, because a drag is one at a time.
  const [moving, setMoving] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);

  const grid = useMemo(() => gridFor(view, anchor), [view, anchor]);
  const { days, from, to } = grid;
  const zone = useZone();
  // "Today" is a square on this grid, so it is the reader's today — a planner in
  // Auckland and one in Los Angeles are on different dates for 19 hours a day.
  const today = useMemo(() => todayIn(zone), [zone]);
  const mayCreate = can("post.create");
  // Choosing the release time is its own authority (ADR-0011), held by a
  // publisher who may not touch the caption and withheld from a creator who
  // may rewrite it.
  const mayReschedule = can("post.schedule");

  function go(next: { view?: View; on?: Date }) {
    const p = new URLSearchParams(params);
    if (next.view) p.set("view", next.view);
    if (next.on) p.set("on", dayKey(next.on));
    setParams(p, { replace: true });
  }

  const load = useCallback(async (quiet: boolean) => {
    if (!workspaceId) return;
    if (!quiet) setLoading(true);
    try {
      const rows = await listCalendarPosts(workspaceId, from, to);
      setPosts(rows);
      setError(null);
      const seen = new Set<string>();
      const targets = rows
        .map((p) => p.media_assets)
        .filter((a): a is NonNullable<typeof a> => !!a)
        // The small copy, and ONLY the small copy (0017-v2 §2). A 26-pixel
        // square is the worst ratio in the product, so an asset with no
        // derivative is not signed at all here — it draws the empty pill
        // rather than a phone camera's original.
        .map(thumbTarget)
        .filter((t): t is NonNullable<typeof t> => t !== null)
        // One entry per object: the same asset on four scheduled posts is
        // one signed URL and must be metered as one transfer, not four.
        .filter((t) => !seen.has(t.storage_path) && seen.add(t.storage_path));
      // Thumbnails are a nicety and signing them is a second round trip: a
      // failure here leaves the placeholder, never an empty calendar.
      setThumbs(await signMedia(targets, { surface: "calendar" }));
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("calendar_view", e, {
        step: "list_calendar_posts",
        workspaceId,
        context: { view, from: from.toISOString(), to: to.toISOString() },
      });
    } finally {
      setLoading(false);
    }
    // `view` is only carried into the log line; refetching on it would double
    // every load, since changing view already changes the window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, from, to]);

  useEffect(() => {
    // The window changed: keep the old grid on screen but stop pretending it is
    // the answer to the question just asked.
    void load(false);
  }, [load]);

  // Deliveries move on the scheduler's clock. Quietly, so a poll never blanks a
  // calendar somebody is reading — and not at all while the tab is hidden
  // (0017-v2 §0A). The media this redraws is signed through `signMedia`, which
  // now reuses a live URL rather than minting a new one, so a poll costs a
  // status query and no transfer.
  usePoll(() => void load(true), 30_000);

  /** The subscription list. Reloaded whenever the picker changes something. */
  const loadCalendars = useCallback(async () => {
    try {
      const [f, s] = await Promise.all([listCalendarFeeds(), listCalendarSubscriptions()]);
      setFeeds(f);
      setSubs(s);
    } catch (e: any) {
      // A holiday overlay that fails to load must never take the posts with it:
      // the calendar's job is the content, and this is what is on it besides.
      void logError("calendar_view", e, { level: "warn", step: "load_subscriptions" });
    }
  }, []);

  useEffect(() => { void loadCalendars(); }, [loadCalendars]);

  /** Feed id → how to draw it and which categories this person asked for. */
  const chosen = useMemo(() => chooseVisible(feeds, subs), [feeds, subs]);

  const feedLookup = useMemo(
    () => new Map(chosen.map((c) => [c.feed.id, {
      name: c.feed.name, hue: c.hue, categories: c.subscription.categories as string[],
    }])),
    [chosen],
  );

  useEffect(() => {
    const ids = chosen.map((c) => c.feed.id);
    if (ids.length === 0) { setEvents([]); return; }

    let live = true;
    listCalendarEvents(ids, from, to)
      .then((rows) => { if (live) setEvents(rows); })
      .catch((e) => {
        void logError("calendar_view", e, { level: "warn", step: "load_events" });
      });
    return () => { live = false; };
  }, [chosen, from, to]);

  const holidays = useMemo(() => holidaysByDay(events, feedLookup), [events, feedLookup]);

  /** Subscribed calendars nobody can currently fetch, named once above the grid. */
  const staleFeeds = useMemo(
    () => chosen.filter((c) => isStale(c.feed)).map((c) => c.feed),
    [chosen],
  );

  const shown = useMemo(() => {
    const status = STATUS_FILTERS.find((f) => f.key === statusFilter) ?? STATUS_FILTERS[0];
    return (posts ?? [])
      .filter(status.match)
      .filter((p) => platformFilter.size === 0 || platformsOf(p).some((x) => platformFilter.has(x)))
      .sort((a, b) => timeOf(a).getTime() - timeOf(b).getTime());
  }, [posts, statusFilter, platformFilter]);

  const byDay = useMemo(() => {
    const map = new Map<string, Post[]>();
    for (const p of shown) {
      const k = dayKeyIn(timeOf(p), zone);
      const list = map.get(k);
      if (list) list.push(p); else map.set(k, [p]);
    }
    return map;
  }, [shown, zone]);

  // Looked up by the SAME key that was signed. Reading `storage_path` here
  // while signing `preview_path` above is how a grid signs derivatives and then
  // renders nothing.
  const thumbFor = (p: Post) => {
    const asset = p.media_assets;
    if (!asset) return undefined;
    const key = thumbPath(asset);
    return key === null ? undefined : thumbs.get(key);
  };

  /**
   * The point of the whole screen: an empty slot is a place to put something.
   *
   * The time travels to Compose as local wall-clock text, which is exactly what
   * its `datetime-local` field speaks — no timezone maths on the way in or out.
   */
  function compose(when: Date) {
    navigate(`/compose?at=${encodeURIComponent(localInput(when))}`);
  }

  /** A day clicked with no hour behind it: the next round hour today, 9am after. */
  function dayDefault(day: Date): Date {
    // All of this is grid-day arithmetic in the reader's zone: `today` is their
    // today and `hourIn` their hour, so nothing here reads the browser's clock.
    if (!sameDay(day, today)) return new Date(day.getFullYear(), day.getMonth(), day.getDate(), 9, 0);
    const hour = hourIn(new Date(), zone);
    if (hour >= 23) return new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 45);
    return new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour + 1, 0);
  }

  const isPast = (day: Date) => day < today;

  /** Only a post that has not started going out can be moved (see the RPC). */
  const canMove = (p: Post) =>
    mayReschedule && (p.status === "draft" || p.status === "scheduled");

  /**
   * Move a post, optimistically.
   *
   * The grid redraws before the round trip, because a drag that visibly snaps
   * back for half a second reads as a bug even when it worked. If the server
   * refuses — the plan's horizon, the wrong role, a delivery already claimed —
   * the post returns to where it was and the refusal is shown in the sentence
   * the database chose.
   */
  const movePost = useCallback(async (postId: string, wall: string) => {
    const before = posts ?? [];
    const target = before.find((p) => p.id === postId);
    if (!target) return;

    // The single place a calendar gesture becomes a moment. Every caller — the
    // drag, the keyboard field in the peek — hands over a wall clock, because
    // that is all a grid square or a `datetime-local` knows.
    const when = wallClockToInstant(wall, zone);

    if (when.getTime() <= Date.now()) {
      throw new Error("Pick a time in the future — publishing now is a different button.");
    }

    setMoveError(null);
    setMoving(postId);
    setPosts(before.map((p) =>
      p.id === postId ? { ...p, scheduled_at: when.toISOString(), status: "scheduled" } : p));

    try {
      const updated = await reschedulePost(postId, when);
      setPosts((current) => (current ?? []).map((p) => (p.id === postId ? { ...p, ...updated } : p)));
      setPeek((current) => (current && current.id === postId ? { ...current, ...updated } : current));
    } catch (e: any) {
      setPosts(before);
      const message = isPaywall(e) ? paywallMessage(e) : (e.message ?? String(e));
      setMoveError(message);
      void logError("post_view", e, {
        level: isPaywall(e) ? "info" : "error",
        step: "reschedule_post",
        workspaceId,
        postId,
        context: { to: when.toISOString(), from: target.scheduled_at },
      });
      throw new Error(message);
    } finally {
      setMoving(null);
    }
  }, [posts, workspaceId, zone]);

  /**
   * A drop lands on a day (month, list) or on a day and an hour (week, day).
   *
   * Dropping onto a date in month view keeps the time of day: a post set for
   * 9am that moves from Tuesday to Thursday is still a 9am post, and silently
   * re-timing it to midnight would be a second, unasked-for edit.
   */
  const dropOn = useCallback((e: React.DragEvent, day: Date, hour: number | null) => {
    e.preventDefault();
    const postId = e.dataTransfer.getData("text/plain");
    if (!postId) return;

    const post = (posts ?? []).find((p) => p.id === postId);
    if (!post) return;

    // Keeping the time of day means keeping it as the READER sees it: a 9am
    // post dragged across the month stays 9am on their clock, not on the
    // browser's, which may be a different hour and occasionally a different day.
    const previous = instantToWallClock(timeOf(post), zone);
    const hh = String(hour ?? Number(previous.slice(11, 13))).padStart(2, "0");
    const mm = hour === null ? previous.slice(14, 16) : "00";
    void movePost(postId, `${dayKey(day)}T${hh}:${mm}`).catch(() => undefined);
  }, [posts, movePost, zone]);

  const dragProps = mayReschedule
    ? {
        onDragOver: (e: React.DragEvent) => {
          // Without this the browser refuses the drop, silently.
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
        },
      }
    : {};


  function step(direction: 1 | -1) {
    if (view === "day") go({ on: addDays(anchor, direction) });
    else if (view === "week") go({ on: addDays(anchor, 7 * direction) });
    else go({ on: addMonths(anchor, direction) });
  }

  if (ready && !can("post.view")) {
    return (
      <div className="card muted">
        Your role in this workspace cannot see posts, so there is no calendar to show.
      </div>
    );
  }

  return (
    <>
      <div className="cal-head">
        <div>
          <h1>Calendar</h1>
          <p className="muted">
            What goes out, and when. Click any empty day or time to plan something for it.
          </p>
        </div>
        {mayCreate && (
          <button className="primary" onClick={() => navigate("/compose")}>New post</button>
        )}
      </div>

      <div className="cal-toolbar">
        <button onClick={() => go({ on: new Date() })}>Today</button>
        <div className="cal-step">
          <button onClick={() => step(-1)} aria-label="Previous">‹</button>
          <button onClick={() => step(1)} aria-label="Next">›</button>
        </div>
        <strong className="cal-heading">{headingFor(view, anchor, days)}</strong>
        {loading && <span className="cal-busy"><Spinner label="Loading the calendar" /></span>}
        <span className="spacer" />
        <div className="segmented" role="group" aria-label="Calendar view">
          {VIEWS.map((v) => (
            <button key={v.key} className={v.key === view ? "on" : ""}
                    aria-pressed={v.key === view}
                    onClick={() => go({ view: v.key })}>
              {v.label}
            </button>
          ))}
        </div>
        {/* One click from "why is Diwali not on here" to the answer. */}
        <button className="cal-calendars" aria-expanded={picking}
                onClick={() => setPicking((on) => !on)}>
          Calendars{chosen.length > 0 ? ` (${chosen.length})` : ""}
        </button>
      </div>

      <div className="cal-filters">
        <label htmlFor="cal-status" className="muted">Show</label>
        <select id="cal-status" value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}>
          {STATUS_FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
        <span className="cal-filter-sep" aria-hidden="true" />
        {PLATFORMS.map((p) => {
          const on = platformFilter.has(p);
          return (
            <button key={p} className={`cal-plat${on ? " on" : ""}`} aria-pressed={on}
                    onClick={() => setPlatformFilter((prev) => {
                      const next = new Set(prev);
                      if (!next.delete(p)) next.add(p);
                      return next;
                    })}>
              <PlatformMark platform={p} /> {PLATFORM_LABEL[p]}
            </button>
          );
        })}
        {(platformFilter.size > 0 || statusFilter !== "all") && (
          <button className="cal-clear"
                  onClick={() => { setPlatformFilter(new Set()); setStatusFilter("all"); }}>
            Clear filters
          </button>
        )}
        <span className="spacer" />
        <span className="muted">
          {shown.length} {shown.length === 1 ? "post" : "posts"} in view
        </span>
      </div>

      {error && <div className="card err">{error}</div>}
      {/* A refused move. Shown here rather than on the pill, which by then has
          snapped back to where it started and has nothing left to say. */}
      {moveError && (
        <div className="card err cal-move-error">
          {moveError}
          <button className="linkish" onClick={() => setMoveError(null)}>Dismiss</button>
        </div>
      )}
      {staleFeeds.length > 0 && (
        <p className="muted cal-note">
          {staleFeeds.length === 1
            ? `${staleFeeds[0].name} could not be updated.`
            : `${staleFeeds.length} calendars could not be updated: ` +
              `${staleFeeds.map((f) => f.name).join(", ")}.`}
          {staleFeeds.some((f) => f.builtin_country)
            ? " Public holidays are still shown from the offline copy."
            : ""}{" "}
          <button className="linkish" onClick={() => setPicking(true)}>Open calendars</button>
        </p>
      )}
      {!mayCreate && (
        <p className="muted cal-note">
          Your role can read this calendar but not add to it — ask a manager in your workspace.
        </p>
      )}

      <div className={`cal-body${loading && posts ? " stale" : ""}`}>
        {view === "month" && (
          <MonthGrid
            days={days} month={anchor.getMonth()} today={today} byDay={byDay}
            holidays={holidays} thumbFor={thumbFor} onPeek={setPeek} mayCreate={mayCreate}
            isPast={isPast} canMove={canMove} moving={moving} dragProps={dragProps}
            onDrop={dropOn}
            onCreate={(d) => compose(dayDefault(d))}
            onExpand={(d) => go({ view: "day", on: d })}
          />
        )}
        {(view === "week" || view === "day") && (
          <TimeGrid
            days={days} today={today} byDay={byDay} holidays={holidays}
            thumbFor={thumbFor} onPeek={setPeek} mayCreate={mayCreate}
            canMove={canMove} moving={moving} dragProps={dragProps} onDrop={dropOn}
            onCreate={(d, hour) =>
              compose(new Date(d.getFullYear(), d.getMonth(), d.getDate(), hour, 0))}
          />
        )}
        {view === "list" && (
          <ListView
            days={days} today={today} byDay={byDay} holidays={holidays}
            thumbFor={thumbFor} onPeek={setPeek} mayCreate={mayCreate} isPast={isPast}
            canMove={canMove} moving={moving} dragProps={dragProps} onDrop={dropOn}
            onCreate={(d) => compose(dayDefault(d))}
          />
        )}
      </div>

      {posts !== null && shown.length === 0 && (
        <div className="card muted">
          No posts {view === "month" || view === "list" ? "this month" : view === "week" ? "this week" : "today"}.
          {mayCreate
            ? <> Pick a day above, or <Link to="/compose">compose a post</Link>.</>
            : null}
          {chosen.length === 0 && (
            <>
              {" "}
              <button className="linkish" onClick={() => setPicking(true)}>
                Add a holiday calendar
              </button>{" "}
              to plan around Diwali, Mother's Day and the rest.
            </>
          )}
        </div>
      )}

      {picking && (
        <CalendarPicker
          feeds={feeds}
          subscriptions={subs}
          onChanged={loadCalendars}
          onClose={() => setPicking(false)}
        />
      )}

      {peek && (
        <PostPeek
          post={peek}
          thumb={thumbFor(peek)}
          moving={moving === peek.id}
          onMove={canMove(peek) ? (when: string) => movePost(peek.id, when) : null}
          onClose={() => setPeek(null)}
        />
      )}
    </>
  );
}

// ------------------------------------------------------------------ views

type CellProps = {
  days: Date[];
  today: Date;
  byDay: Map<string, Post[]>;
  /** Holidays and festivals, keyed the same way the posts are. */
  holidays: Map<string, Holiday[]>;
  thumbFor: (p: Post) => string | undefined;
  onPeek: (p: Post) => void;
  mayCreate: boolean;
  canMove: (p: Post) => boolean;
  /** The post currently being moved, so its pill can show the ring. */
  moving: string | null;
  /** onDragOver, or nothing at all when this person may not reschedule. */
  dragProps: Record<string, unknown>;
  onDrop: (e: React.DragEvent, day: Date, hour: number | null) => void;
};

/** The one line every draggable pill needs, kept in one place. */
function dragHandlers(post: Post, canMove: (p: Post) => boolean) {
  if (!canMove(post)) return {};
  return {
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      e.dataTransfer.setData("text/plain", post.id);
      e.dataTransfer.effectAllowed = "move";
    },
  };
}

function MonthGrid({
  days, month, today, byDay, holidays, thumbFor, onPeek, mayCreate, isPast,
  canMove, moving, dragProps, onDrop, onCreate, onExpand,
}: CellProps & {
  month: number;
  isPast: (d: Date) => boolean;
  onCreate: (d: Date) => void;
  onExpand: (d: Date) => void;
}) {
  return (
    <div className="cal-month" role="grid" aria-label="Month">
      {days.slice(0, 7).map((d) => (
        <div key={`h${d.getDay()}`} className="cal-colhead">
          {d.toLocaleDateString(undefined, { weekday: "short" })}
        </div>
      ))}
      {days.map((day) => {
        const events = byDay.get(dayKey(day)) ?? [];
        const extra = events.length - MAX_PER_CELL;
        const outside = day.getMonth() !== month;
        const past = isPast(day);
        const openable = mayCreate && !past;
        const dayText = day.toLocaleDateString(undefined,
          { weekday: "long", month: "long", day: "numeric" });
        const dayHolidays = holidays.get(dayKey(day)) ?? [];
        return (
          <div key={dayKey(day)}
               className={`cal-cell${outside ? " outside" : ""}${sameDay(day, today) ? " today" : ""}${past ? " past" : ""}`}
               {...dragProps}
               onDrop={(e) => onDrop(e, day, null)}>
            {/* The empty part of the cell is the control. A div with an onClick
                would be invisible to a keyboard, and this is the primary way
                anything gets planned. */}
            {openable && (
              <button className="cal-cell-hit" onClick={() => onCreate(day)}
                      aria-label={`Plan a post for ${dayText}`} />
            )}
            <div className="cal-cell-inner">
              <div className="cal-cell-head">
                <span className="cal-daynum">{day.getDate()}</span>
                {openable && (
                  <button className="cal-add" onClick={() => onCreate(day)}
                          aria-label={`Plan a post for ${dayText}`}>+</button>
                )}
              </div>
              {/* Above the posts, because the day is the reason for the post
                  and not the other way round. */}
              {dayHolidays.length > 0 && (
                <div className="cal-holidays">
                  <HolidayChips chips={dayHolidays} max={2}
                                onPlan={openable ? () => onCreate(day) : null} />
                </div>
              )}
              {events.slice(0, MAX_PER_CELL).map((p) => (
                <EventPill key={p.id} post={p} thumb={thumbFor(p)} onOpen={() => onPeek(p)}
                           busy={moving === p.id} {...dragHandlers(p, canMove)} />
              ))}
              {extra > 0 && (
                <button className="cal-more" onClick={() => onExpand(day)}>
                  +{extra} more
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Week and day are the same grid with a different number of columns.
 *
 * Events are placed by the minute rather than dropped into an hour bucket,
 * because "10:10 and 10:18" is a real collision an operator needs to see, and
 * two pills stacked in an hour row would hide it.
 */
function TimeGrid({
  days, today, byDay, holidays, thumbFor, onPeek, mayCreate,
  canMove, moving, dragProps, onDrop, onCreate,
}: CellProps & { onCreate: (d: Date, hour: number) => void }) {
  const zone = useZone();
  const scroller = useRef<HTMLDivElement>(null);

  const firstEventHour = useMemo(() => {
    let earliest: number | null = null;
    for (const day of days) {
      for (const p of byDay.get(dayKey(day)) ?? []) {
        const h = hourIn(timeOf(p), zone);
        if (earliest === null || h < earliest) earliest = h;
      }
    }
    return earliest;
  }, [days, byDay, zone]);

  useEffect(() => {
    // Open where the day starts, or earlier if something is scheduled before it —
    // an event above the fold on load is an event nobody sees.
    const hour = Math.max(0, Math.min(OPENING_HOUR, firstEventHour ?? OPENING_HOUR) - 1);
    if (scroller.current) scroller.current.scrollTop = hour * HOUR_PX;
  }, [firstEventHour, days.length]);

  const now = new Date();

  return (
    // One column per day, week and day view alike — the grid template is the
    // only difference between the two.
    <div className="cal-timewrap" style={{ "--cal-days": days.length } as React.CSSProperties}>
      <div className="cal-timehead">
        <div className="cal-gutter-head" />
        {days.map((d) => (
          <div key={dayKey(d)} className={`cal-colhead${sameDay(d, today) ? " today" : ""}`}>
            <span>{d.toLocaleDateString(undefined, { weekday: "short" })}</span>
            <strong>{d.getDate()}</strong>
          </div>
        ))}
      </div>

      {/* The all-day row. Present only when there is something in it: an empty
          band above every week is a permanent tax on the hours below. */}
      {days.some((d) => (holidays.get(dayKey(d)) ?? []).length > 0) && (
        <div className="cal-allday">
          <div className="cal-allday-label muted">All day</div>
          {days.map((d) => (
            <div key={dayKey(d)} className="cal-allday-cell">
              <HolidayChips
                chips={holidays.get(dayKey(d)) ?? []}
                onPlan={mayCreate ? () => onCreate(d, 9) : null}
              />
            </div>
          ))}
        </div>
      )}
      <div className="cal-timescroll" ref={scroller}>
        <div className="cal-gutter">
          {HOURS.map((h) => (
            <div key={h} className="cal-hourlabel" style={{ height: HOUR_PX }}>
              <span>{hourText(h)}</span>
            </div>
          ))}
        </div>
        {days.map((day) => {
          const placed = pack(byDay.get(dayKey(day)) ?? [], zone);
          const dayText = day.toLocaleDateString(undefined,
            { weekday: "long", month: "long", day: "numeric" });
          return (
            <div key={dayKey(day)} className="cal-daycol">
              {HOURS.map((h) => {
                const slot = new Date(day.getFullYear(), day.getMonth(), day.getDate(), h, 0);
                const openable = mayCreate && slot.getTime() > now.getTime() - 60 * 60_000;
                return (
                  <div key={h} className="cal-slot" style={{ height: HOUR_PX }}
                       {...dragProps} onDrop={(e) => onDrop(e, day, h)}>
                    {openable && (
                      <button className="cal-slot-hit" onClick={() => onCreate(day, h)}
                              aria-label={`Plan a post for ${dayText} at ${hourText(h)}`} />
                    )}
                  </div>
                );
              })}
              {sameDay(day, today) && (
                <div className="cal-nowline" aria-hidden="true"
                     style={{ top: (minutesIn(now, zone) / 60) * HOUR_PX }} />
              )}
              {placed.map(({ post, index, of }) => {
                const minutes = minutesIn(timeOf(post), zone);
                return (
                  <EventPill
                    key={post.id} post={post} thumb={thumbFor(post)} onOpen={() => onPeek(post)}
                    busy={moving === post.id} {...dragHandlers(post, canMove)}
                    className="placed"
                    style={{
                      top: (minutes / 60) * HOUR_PX,
                      height: (SLOT_MINUTES / 60) * HOUR_PX - 3,
                      left: `${(index / of) * 100}%`,
                      width: `calc(${100 / of}% - 4px)`,
                    }}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Events that would sit on top of each other, split into columns.
 *
 * A run of posts is one group while each new one starts inside the last one's
 * slot; everything in a group shares the width. Not a general interval packer —
 * publishing times are points, and this is the whole of the geometry.
 */
function pack(events: Post[], zone: string): Array<{ post: Post; index: number; of: number }> {
  const sorted = [...events].sort((a, b) => timeOf(a).getTime() - timeOf(b).getTime());
  const out: Array<{ post: Post; index: number; of: number }> = [];
  let group: Post[] = [];
  let groupEnd = -Infinity;

  const flush = () => {
    group.forEach((post, index) => out.push({ post, index, of: group.length }));
    group = [];
  };

  for (const p of sorted) {
    const start = minutesIn(timeOf(p), zone);
    if (group.length > 0 && start < groupEnd) {
      group.push(p);
      groupEnd = Math.max(groupEnd, start + SLOT_MINUTES);
    } else {
      flush();
      group = [p];
      groupEnd = start + SLOT_MINUTES;
    }
  }
  flush();
  return out;
}

/** The month as a run of days, for reading rather than for planning geometry. */
function ListView({
  days, today, byDay, holidays, thumbFor, onPeek, mayCreate, isPast,
  canMove, moving, dragProps, onDrop, onCreate,
}: CellProps & { isPast: (d: Date) => boolean; onCreate: (d: Date) => void }) {
  // A day earns a row if it has a post OR a holiday: "nothing scheduled for
  // Diwali" is the single most useful line this view can show a planner.
  const busy = days.filter((d) =>
    (byDay.get(dayKey(d)) ?? []).length > 0 || (holidays.get(dayKey(d)) ?? []).length > 0);
  if (busy.length === 0) return null;

  return (
    <div className="cal-list">
      {busy.map((day) => (
        <div key={dayKey(day)} className={`cal-listday${sameDay(day, today) ? " today" : ""}`}
             {...dragProps} onDrop={(e) => onDrop(e, day, null)}>
          <div className="cal-listdate">
            <strong>{day.getDate()}</strong>
            <span className="muted">{day.toLocaleDateString(undefined, { weekday: "short" })}</span>
            {mayCreate && !isPast(day) && (
              <button className="cal-add" onClick={() => onCreate(day)}
                      aria-label={`Plan a post for ${day.toLocaleDateString(undefined, { month: "long", day: "numeric" })}`}>
                +
              </button>
            )}
          </div>
          <div className="cal-listevents">
            {(holidays.get(dayKey(day)) ?? []).length > 0 && (
              <div className="cal-holidays">
                <HolidayChips
                  chips={holidays.get(dayKey(day)) ?? []}
                  onPlan={mayCreate && !isPast(day) ? () => onCreate(day) : null}
                />
              </div>
            )}
            {(byDay.get(dayKey(day)) ?? []).map((p) => (
              <EventPill key={p.id} post={p} thumb={thumbFor(p)} onOpen={() => onPeek(p)}
                         className="wide" busy={moving === p.id} {...dragHandlers(p, canMove)} />
            ))}
            {(byDay.get(dayKey(day)) ?? []).length === 0 && (
              <p className="muted cal-list-nothing">Nothing planned.</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
